import { MeResponse } from "@telecurb/shared";
import type {
  LoginResponse,
  MfaChallengeResponse,
  MfaEnrollRequest,
  MfaEnrollResponse,
} from "@telecurb/shared";
import { ApiError, api } from "@/lib/api";
import {
  type AuthClient,
  type AcceptInviteProfilePatch,
  type InvitePreview,
  type LoginStartResult,
  InvalidCredentialsError,
  InvalidInvitationError,
  InvalidMfaCodeError,
  InvalidResetTokenError,
  InvalidPhoneError,
  MfaChallengeExpiredError,
  MfaRestartNotAllowedError,
  MfaSessionExpiredError,
  MissingActorError,
  WeakPasswordError,
} from "./client";

// The { error } code carried by a 401 from the MFA endpoints.
function errorCode(body: unknown): string | undefined {
  if (typeof body === "object" && body !== null && "error" in body) {
    const code = (body as { error?: unknown }).error;
    return typeof code === "string" ? code : undefined;
  }
  return undefined;
}

// WorkOS-backed `AuthClient`. The only frontend file a future provider swap
// touches. All requests rely on the `tc_session` HttpOnly cookie set by the
// backend; the client never reads or writes the cookie directly.
export const workosAuthClient: AuthClient = {
  async login(email, password): Promise<LoginStartResult> {
    try {
      // No session cookie is set here — the response carries the next MFA step.
      return await api<LoginResponse>("/api/v1/auth/login", {
        method: "POST",
        body: JSON.stringify({ email, password }),
      });
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        throw new InvalidCredentialsError();
      }
      throw err;
    }
  },

  async enrollMfa(req: MfaEnrollRequest): Promise<MfaEnrollResponse> {
    try {
      return await api<MfaEnrollResponse>("/api/v1/auth/mfa/enroll", {
        method: "POST",
        body: JSON.stringify(req),
      });
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        throw new MfaSessionExpiredError();
      }
      if (err instanceof ApiError && err.status === 422) {
        throw new InvalidPhoneError();
      }
      throw err;
    }
  },

  async resendMfaChallenge(): Promise<MfaChallengeResponse> {
    try {
      return await api<MfaChallengeResponse>("/api/v1/auth/mfa/challenge", {
        method: "POST",
      });
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        throw new MfaSessionExpiredError();
      }
      throw err;
    }
  },

  async restartMfaEnrollment() {
    try {
      await api<void>("/api/v1/auth/mfa/restart", {
        method: "POST",
      });
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        throw new MfaSessionExpiredError();
      }
      if (err instanceof ApiError && err.status === 409) {
        throw new MfaRestartNotAllowedError();
      }
      throw err;
    }
  },

  async verifyMfa(challengeId, code, rememberDevice) {
    try {
      await api<{ status: "authenticated" }>("/api/v1/auth/mfa/verify", {
        method: "POST",
        body: JSON.stringify({ challengeId, code, rememberDevice }),
      });
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        const reason = errorCode(err.body);
        if (reason === "mfa_challenge_expired") throw new MfaChallengeExpiredError();
        if (reason === "mfa_session_expired") throw new MfaSessionExpiredError();
        throw new InvalidMfaCodeError();
      }
      throw err;
    }
  },

  async logout() {
    // 204 No Content; `api()` handles the empty body and respects the API base URL.
    await api<void>("/api/v1/auth/logout", { method: "POST" });
  },

  async getInvite(token): Promise<InvitePreview> {
    try {
      return await api<InvitePreview>(`/api/v1/auth/invite?token=${encodeURIComponent(token)}`);
    } catch (err) {
      if (err instanceof ApiError && (err.status === 404 || err.status === 401)) {
        throw new InvalidInvitationError();
      }
      throw err;
    }
  },

  async acceptInvite(token, password, profilePatch?: AcceptInviteProfilePatch) {
    try {
      // Server now returns mfa_enrollment_required (no session) — the caller
      // transitions into SMS enrollment. We only need success vs. error here.
      await api<{ status: "mfa_enrollment_required" }>("/api/v1/auth/accept-invite", {
        method: "POST",
        body: JSON.stringify({ token, password, ...profilePatch }),
      });
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        throw new InvalidInvitationError();
      }
      if (err instanceof ApiError && err.status === 422) {
        // The server returns { error: "weak_password" } for password failures
        // and { field: "phone", message: "..." } for profile validation failures.
        // Only translate to WeakPasswordError when it's clearly a password issue.
        const body = err.body;
        if (body !== null && typeof body === "object" && !Array.isArray(body) && "field" in body) {
          throw err; // re-throw as ApiError so callers can inspect the field error
        }
        throw new WeakPasswordError();
      }
      throw err;
    }
  },

  async resetPassword(token, password) {
    try {
      // 204 No Content on success — no session is minted; the user signs in
      // with the new password afterwards.
      await api<void>("/api/v1/auth/password-reset", {
        method: "POST",
        body: JSON.stringify({ token, password }),
      });
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        throw new InvalidResetTokenError();
      }
      if (err instanceof ApiError && err.status === 422) {
        throw new WeakPasswordError();
      }
      throw err;
    }
  },

  async getCurrentUser() {
    try {
      const res = await api<unknown>("/api/v1/me");
      return MeResponse.parse(res);
    } catch (err) {
      if (err instanceof ApiError && err.status === 401) {
        // A missing actor means "authenticated upstream but no usable account
        // here" — surface it as an error instead of collapsing to null (which
        // reads as "signed out" and silently bounces to /sign-in). Other 401s
        // are genuine sign-outs → null.
        if (isMissingActor(err.body)) throw new MissingActorError();
        return null;
      }
      throw err;
    }
  },
};

function isMissingActor(body: unknown): boolean {
  return (
    typeof body === "object" &&
    body !== null &&
    "error" in body &&
    (body as { error?: unknown }).error === "missing_actor"
  );
}
